import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import API from '../../../services/API'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import FeedbackDrawer from './FeedbackDrawer'
import { statusColor } from './feedbackConstants'
// MUI
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import LinkIcon from '@mui/icons-material/Link'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'

const PRESETS = [
  {
    key: 'new',
    label: 'New',
    caption: 'Not yet reviewed',
    tone: 'error',
    match: (f) => (f.status ?? 'New') === 'New',
  },
  {
    key: 'reviewing',
    label: 'Reviewing',
    caption: 'Being looked into',
    tone: 'warning',
    match: (f) => f.status === 'Reviewing',
  },
  {
    key: 'completed',
    label: 'Completed',
    caption: 'Closed off',
    tone: 'success',
    match: (f) => f.status === 'Completed',
  },
  {
    key: 'compliments',
    label: 'Compliments',
    caption: 'Marked as praise',
    tone: 'success',
    match: (f) => !!f.isComplement,
  },
  {
    key: 'complaints',
    label: 'Complaints',
    caption: 'Marked as concerns',
    tone: 'error',
    match: (f) => !!f.isComplaint,
  },
  {
    key: 'all',
    label: 'All Feedback',
    caption: 'Everything received',
    tone: 'primary',
    match: () => true,
  },
]

const PresetTile = ({ preset, count, active, onClick }) => (
  <Card
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 1.5,
      borderRadius: 2.5,
      cursor: 'pointer',
      minWidth: 0,
      borderColor: active ? 'primary.main' : 'divider',
      borderWidth: active ? 2 : 1,
      bgcolor: active ? 'action.hover' : 'background.paper',
      transition: 'all 0.15s ease',
      '&:hover': { borderColor: 'primary.light' },
    }}
  >
    <Typography
      component="div"
      variant="caption"
      noWrap
      sx={{
        color: 'text.secondary',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '0.66rem',
      }}
    >
      {preset.label}
    </Typography>
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: `${preset.tone}.main`,
          flexShrink: 0,
        }}
      />
      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }} noWrap>
        {count ?? '—'}
      </Typography>
    </Stack>
    <Typography component="div" variant="caption" color="text.secondary" noWrap>
      {preset.caption}
    </Typography>
  </Card>
)

// Share of responses answering "yes" to each experience question, plus the
// Friends & Family recommend rate — replaces the old net +1/−1 bar chart.
const QUESTIONS = [
  { key: 'kindHelpful', label: 'Staff were kind and helpful' },
  { key: 'dignityRespect', label: 'Treated with dignity and respect' },
  { key: 'involved', label: 'Involved in decisions about care' },
  { key: 'confidenceTrust', label: 'Confidence and trust in staff' },
]

const pct = (num, den) => (den ? Math.round((num / den) * 100) : null)

const ExperienceSummary = ({ feedbacks }) => {
  const total = feedbacks.length
  const recommendYes = feedbacks.filter((f) => f.recommend === 'Yes').length
  const recommendRate = pct(recommendYes, total)

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2.5,
        border: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={{ xs: 2, md: 4 }}
        alignItems={{ md: 'center' }}
      >
        <Box minWidth={{ md: 190 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontSize: '0.66rem',
            }}
          >
            Would recommend us
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '2.2rem', lineHeight: 1.1 }}>
            {recommendRate === null ? '—' : `${recommendRate}%`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {total
              ? `${recommendYes} of ${total} response${total === 1 ? '' : 's'}`
              : 'No responses yet'}
          </Typography>
        </Box>
        <Box
          flex={1}
          display="grid"
          gap={1.25}
          gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
        >
          {QUESTIONS.map((q) => {
            const yes = feedbacks.filter((f) => !!f[q.key]).length
            const rate = pct(yes, total)
            return (
              <Box key={q.key} minWidth={0}>
                <Stack direction="row" justifyContent="space-between" spacing={1}>
                  <Typography variant="body2" noWrap>
                    {q.label}
                  </Typography>
                  <Typography variant="body2" fontWeight={700} flexShrink={0}>
                    {rate === null ? '—' : `${rate}%`}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={rate ?? 0}
                  color={rate === null ? 'inherit' : rate >= 80 ? 'success' : rate >= 50 ? 'warning' : 'error'}
                  sx={{ height: 6, borderRadius: 999, mt: 0.5 }}
                />
              </Box>
            )
          })}
        </Box>
      </Stack>
    </Paper>
  )
}

const Feedback = () => {
  const [patientFeedbacks, setPatientFeedbacks] = useState([])
  const [loading, setLoading] = useState(true)
  const [failedFetch, setFailedFetch] = useState(false)
  const [selectedFeedback, setSelectedFeedback] = useState(null)
  const [activePreset, setActivePreset] = useState('new')
  const [search, setSearch] = useState('')
  const [relationship, setRelationship] = useState('')
  const [contactType, setContactType] = useState('')
  const [linkedOnly, setLinkedOnly] = useState(false)

  const loadFeedback = () => {
    API.get('/patient-feedback')
      .then((res) => {
        if (res.status === 200) {
          setPatientFeedbacks(res.data)
          setFailedFetch(false)
        } else {
          setFailedFetch(true)
        }
      })
      .catch(() => setFailedFetch(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadFeedback()
  }, [])

  const sorted = useMemo(
    () =>
      [...patientFeedbacks].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      ),
    [patientFeedbacks],
  )

  const counts = useMemo(
    () =>
      PRESETS.reduce((acc, p) => {
        acc[p.key] = sorted.filter(p.match).length
        return acc
      }, {}),
    [sorted],
  )

  const last30 = useMemo(
    () =>
      sorted.filter((f) =>
        moment(f.createdAt).isAfter(moment().subtract(30, 'days')),
      ).length,
    [sorted],
  )

  const filtered = useMemo(() => {
    const preset = PRESETS.find((p) => p.key === activePreset) ?? PRESETS[5]
    const term = search.trim().toLowerCase()
    return sorted.filter((f) => {
      if (!preset.match(f)) return false
      if (relationship && f.relationship !== relationship) return false
      if (contactType && f.contactType !== contactType) return false
      if (linkedOnly && !f.epcr) return false
      if (term) {
        const hay = [
          f.feedbackComments,
          f.firstName,
          f.lastName,
          f.email,
          f.epcr?.epcrNumber,
          f.learning,
          ...(f.staff ?? []).map(
            (s) => `${s.userAccount?.firstName ?? ''} ${s.userAccount?.lastName ?? ''}`,
          ),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        if (!hay.includes(term)) return false
      }
      return true
    })
  }, [sorted, activePreset, search, relationship, contactType, linkedOnly])

  const columns = [
    {
      headerName: 'Received',
      field: 'createdAt',
      minWidth: 120,
      renderCell: ({ row }) => (
        <Tooltip title={moment(row.createdAt).format('Do MMM YYYY [at] HH:mm')}>
          <Typography variant="body2">
            {moment(row.createdAt).format('DD/MM/YYYY')}
          </Typography>
        </Tooltip>
      ),
    },
    {
      headerName: 'Status',
      field: 'status',
      minWidth: 120,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={row.status ?? 'New'}
          color={statusColor(row.status)}
          sx={{ borderRadius: 1, fontWeight: 600 }}
        />
      ),
    },
    {
      headerName: 'Type',
      field: 'type',
      minWidth: 130,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          {row.isComplement && (
            <Chip
              size="small"
              icon={<ThumbUpIcon />}
              label="Compliment"
              color="success"
              variant="outlined"
              sx={{ borderRadius: 1 }}
            />
          )}
          {row.isComplaint && (
            <Chip
              size="small"
              icon={<ThumbDownIcon />}
              label="Complaint"
              color="error"
              variant="outlined"
              sx={{ borderRadius: 1 }}
            />
          )}
          {!row.isComplement && !row.isComplaint && (
            <Chip
              size="small"
              label="Untriaged"
              variant="outlined"
              sx={{ borderRadius: 1 }}
            />
          )}
        </Stack>
      ),
    },
    {
      headerName: 'Feedback',
      field: 'feedbackComments',
      flex: 1,
      minWidth: 260,
      renderCell: ({ row }) => (
        <Tooltip title={row.feedbackComments ?? ''}>
          <Typography
            variant="body2"
            noWrap
            color={row.feedbackComments ? 'text.primary' : 'text.disabled'}
          >
            {row.feedbackComments || 'No written comments'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      headerName: 'Recommend',
      field: 'recommend',
      minWidth: 110,
      renderCell: ({ row }) =>
        row.recommend ? (
          <Chip
            size="small"
            label={row.recommend}
            color={
              row.recommend === 'Yes'
                ? 'success'
                : row.recommend === 'No'
                  ? 'error'
                  : 'default'
            }
            variant="outlined"
            sx={{ borderRadius: 1 }}
          />
        ) : (
          '—'
        ),
    },
    {
      headerName: 'From',
      field: 'relationship',
      minWidth: 150,
      renderCell: ({ row }) => (
        <Tooltip title={[row.relationship, row.contactType].filter(Boolean).join(' · ')}>
          <Typography variant="body2" noWrap>
            {row.relationship || '—'}
            {row.contactType && (
              <Typography component="span" variant="caption" color="text.secondary">
                {' '}· {row.contactType}
              </Typography>
            )}
          </Typography>
        </Tooltip>
      ),
    },
    {
      headerName: 'Care Record',
      field: 'epcr',
      minWidth: 130,
      sortable: false,
      renderCell: ({ row }) =>
        row.epcr ? (
          <Chip
            size="small"
            icon={<LinkIcon />}
            label={row.epcr.epcrNumber ? `ePCR ${row.epcr.epcrNumber}` : 'Linked'}
            color="anBlue"
            sx={{ borderRadius: 1, fontWeight: 600 }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            Anonymous
          </Typography>
        ),
    },
  ]

  return (
    <Stack gap={1.5}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        gap={1}
      >
        <Box>
          <Typography variant="title2" component="div">
            Patient Feedback
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {loading
              ? ''
              : `${last30} response${last30 === 1 ? '' : 's'} in the last 30 days`}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<OpenInNewIcon />}
          onClick={() => window.open('/patient-feedback', '_blank', 'noopener')}
          sx={{ borderRadius: 2, textTransform: 'none' }}
        >
          Open public form
        </Button>
      </Stack>

      <Box
        display="grid"
        gap={1.5}
        gridTemplateColumns={{
          xs: '1fr 1fr',
          sm: '1fr 1fr 1fr',
          lg: 'repeat(6, 1fr)',
        }}
      >
        {PRESETS.map((preset) => (
          <PresetTile
            key={preset.key}
            preset={preset}
            count={loading ? null : counts[preset.key]}
            active={activePreset === preset.key}
            onClick={() => setActivePreset(preset.key)}
          />
        ))}
      </Box>

      <ExperienceSummary feedbacks={sorted} />

      <Paper
        elevation={0}
        sx={{
          p: 1.25,
          borderRadius: 2.5,
          border: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" useFlexGap>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            placeholder="Search comments, names, ePCR number, crew…"
            sx={{ flex: 1, minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
            label="From"
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">Anyone</MenuItem>
            {['Patient', 'Relative', 'Carer', 'Other'].map((r) => (
              <MenuItem key={r} value={r}>
                {r}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            value={contactType}
            onChange={(e) => setContactType(e.target.value)}
            label="Where"
            size="small"
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Anywhere</MenuItem>
            {['At an Event', 'Hospital', 'My Home', 'Somewhere else'].map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          <Chip
            icon={<LinkIcon />}
            label="Linked to a care record"
            clickable
            color={linkedOnly ? 'anBlue' : 'default'}
            variant={linkedOnly ? 'filled' : 'outlined'}
            onClick={() => setLinkedOnly((v) => !v)}
            sx={{ borderRadius: 999, fontWeight: 600 }}
          />
        </Stack>
      </Paper>

      {loading && <LinearProgress />}
      {failedFetch && (
        <Alert severity="error">
          Unable to load patient feedback. Please try again later.
        </Alert>
      )}

      <CustomDataGrid
        norowsoverlay={
          <Alert severity="info" icon={<ForumOutlinedIcon />}>
            {patientFeedbacks.length === 0
              ? 'No feedback received yet. Patients who consent on their ePCR are texted a feedback link automatically when the record is submitted; anyone can also use the public form.'
              : 'No feedback matches these filters.'}
          </Alert>
        }
        rows={filtered}
        columns={columns}
        getRowId={(row) => row._id}
        pagination
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
        disableRowSelectionOnClick
        onRowClick={({ row }) => setSelectedFeedback(row)}
        sx={{
          '.MuiDataGrid-cell:focus': { outline: 'none' },
          '& .MuiDataGrid-row:hover': { cursor: 'pointer' },
        }}
        slots={{ toolbar: () => null }}
      />

      <FeedbackDrawer
        selectedFeedback={selectedFeedback}
        setSelectedFeedback={setSelectedFeedback}
        loadFeedback={loadFeedback}
      />
    </Stack>
  )
}

export default Feedback
